import { ID } from "@datassential/platform-ui-lib";
import { components } from "@apiSchema";
import { iPermissionFields, PermissionLevelEnum, PermissionSection } from "@models/PermissionSection";

export interface IFoodProfileMenuData {
    menuVersatility: number;
    foodVersatility: number;
    hasLocalPopularity: boolean;
    hasConsumerPreferences: boolean;
    pairedFlavors: Array<{
        id: ID;
        name: string;
        value: number;
    }>;
}

export interface IFoodProfileMenu extends iPermissionFields {
    id: ID;
    name: string;
    data: IFoodProfileMenuData;
}

type ApiModel = components["schemas"]["FoodProfileMenuTrendsAndFlavorDataViewModel"];

interface IData {
    apiModel?: ApiModel;
}

export class FoodProfileMenu implements IFoodProfileMenu {
    static readonly defaultData: IFoodProfileMenuData = {
        menuVersatility: 0,
        foodVersatility: 0,
        pairedFlavors: [],
        hasLocalPopularity: false,
        hasConsumerPreferences: false,
    }

    static readonly fakeData: IFoodProfileMenuData = {
        menuVersatility: 50,
        foodVersatility: 50,
        hasLocalPopularity: true,
        hasConsumerPreferences: true,
        pairedFlavors: [
            { id: 1, name: 'Flavor 1', value: .3 },
            { id: 2, name: 'Flavor 2', value: .4 },
            { id: 3, name: 'Flavor 3', value: .5 },
            { id: 4, name: 'Flavor 4', value: .6 },
            { id: 5, name: 'Flavor 5', value: .7 },
            { id: 6, name: 'Flavor 6', value: .8 },
            { id: 7, name: 'Flavor 7', value: .9 },
            { id: 8, name: 'Flavor 8', value: .10 },
            { id: 9, name: 'Flavor 9', value: .11 },
            { id: 10, name: 'Flavor 10', value: .12 },
        ],
    }

    id = 0;
    name = '';
    permissionLevel = PermissionLevelEnum.Full;
    hasData = true;
    data = {
        ...FoodProfileMenu.defaultData,
    }

    constructor(data?: IData) {
        if (data?.apiModel) {
            this.mapFromApiModel(data.apiModel);
        }
    }

    private setData(model: Partial<IFoodProfileMenu>) {
        Object.assign(this, model);
    }

    private mapFromApiModel(apiModel: ApiModel) {
        const {
            foodProfileId,
            foodProfileWord,
            menuTrendsData,
            consumerPreferencesData,
        } = apiModel;

        const permissionLevel = PermissionSection.getPermissionLevel(menuTrendsData?.permissionLevel);
        this.hasData = !!menuTrendsData?.data;

        this.setData({
            id: foodProfileId ?? 0,
            name: foodProfileWord ?? '',
            permissionLevel,
            hasData: this.hasData,
            data: permissionLevel === PermissionLevelEnum.Full ? {
                menuVersatility: menuTrendsData?.data?.menuVersatility ?? FoodProfileMenu.defaultData.menuVersatility,
                foodVersatility: menuTrendsData?.data?.foodVersatility ?? FoodProfileMenu.defaultData.foodVersatility,
                pairedFlavors: this.mapPairedFlavors(apiModel) ?? FoodProfileMenu.defaultData.pairedFlavors,
                hasLocalPopularity: menuTrendsData?.data?.isLocal ?? false,
                hasConsumerPreferences: consumerPreferencesData?.consumerPreferencesWordData?.hasData ?? false,
            } : {
                ...FoodProfileMenu.fakeData,
                hasConsumerPreferences: consumerPreferencesData?.consumerPreferencesWordData?.hasData ?? false,
                hasLocalPopularity: menuTrendsData?.data?.isLocal ?? true,
            },
        });
    }

    private mapPairedFlavors(apiModel: ApiModel): IFoodProfileMenuData['pairedFlavors'] {
        return (apiModel.menuTrendsData?.data?.pairedFlavors ?? []).map((i, index) => ({
            id: i.id ?? 0,
            name: i.name ?? '',
            value: +(i.value ?? 0),
        }));
    }
}